import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api, errMsg, inr, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/components/admin/auth";
import { PageTitle, Panel, Stat, StatusBadge } from "@/components/admin/AdminLayout";
import { StoreClicks } from "@/components/admin/StoreClicks";

const List = ({ rows, money, testid }) => (
  <ul className="space-y-2 text-sm" data-testid={testid}>
    {rows.map((r) => <li key={r.key} className="flex justify-between gap-3"><span className="truncate">{r.key}</span><span className="font-semibold shrink-0">{money ? inr(r.inr) : r.count}</span></li>)}
    {!rows.length && <li className="text-slate-500">No data yet.</li>}
  </ul>
);

export default function Earnings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const fileRef = useRef();
  const [days, setDays] = useState(30);
  const { data: a } = useQuery({ queryKey: ["analytics", days], queryFn: () => api.get(`/admin/analytics?days=${days}`).then((r) => r.data) });
  const { data: c } = useQuery({ queryKey: ["commissions"], queryFn: () => api.get("/admin/commissions").then((r) => r.data) });
  const upload = async (e) => {
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    try {
      const r = await api.post("/admin/commissions/import", fd);
      toast.success(`${r.data.created} new, ${r.data.updated} updated, ${r.data.failed} failed`);
      qc.invalidateQueries({ queryKey: ["commissions"] }); qc.invalidateQueries({ queryKey: ["analytics"] });
    } catch (er) { toast.error(errMsg(er)); } finally { e.target.value = ""; }
  };
  return (
    <div className="space-y-6" data-testid="admin-earnings-page">
      <PageTitle title="Clicks & Earnings" sub="Clicks are counted by our redirect. Commissions come only from network reports - some networks don't share order-level data, so conversion figures may be incomplete.">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-10 rounded-lg border border-slate-200 px-3 text-sm" data-testid="earnings-range-select">
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </PageTitle>
      {a && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat label="Clicks" value={a.clicks} testid="earn-clicks" />
            <Stat label="Conversions reported" value={a.conversions} hint={`${a.conversion_rate_pct}% of clicks`} testid="earn-conversions" />
            <Stat label="Estimated (pending)" value={inr(a.estimated_inr)} tone="amber" testid="earn-estimated" />
            <Stat label="Confirmed" value={inr(a.confirmed_inr)} tone="green" testid="earn-confirmed" />
            <Stat label="Reversed / rejected" value={inr(a.reversed_inr)} tone="red" testid="earn-reversed" />
          </div>
          <Panel title="Clicks per day" testid="clicks-chart">
            <div className="h-56"><ResponsiveContainer><BarChart data={a.daily}><XAxis dataKey="date" fontSize={10} /><YAxis fontSize={10} width={30} allowDecimals={false} /><Tooltip /><Bar dataKey="clicks" fill="#059669" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          </Panel>
          <div className="grid md:grid-cols-3 gap-6">
            <Panel title="Clicks by traffic source"><List rows={a.by_source} testid="clicks-by-source" /></Panel>
            <Panel title="Clicks by merchant"><List rows={a.by_merchant} testid="clicks-by-merchant" /></Panel>
            <Panel title="Top deals by clicks"><List rows={a.by_deal} testid="clicks-by-deal" /></Panel>
            <Panel title="Earnings by merchant"><List rows={a.earnings_by_merchant} money testid="earnings-by-merchant" /></Panel>
            <Panel title="Earnings by traffic source"><List rows={a.earnings_by_source} money testid="earnings-by-source" /></Panel>
          </div>
          <StoreClicks />
        </>
      )}
      <Panel title="Commission records" testid="commissions-panel" action={user.role === "admin" && (
        <>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={upload} data-testid="commission-file-input" />
          <button onClick={() => fileRef.current.click()} data-testid="commission-upload-button" className="text-sm bg-slate-900 text-white px-3 py-1.5 rounded-lg flex items-center gap-1"><Upload size={14} /> Import report CSV</button>
        </>
      )}>
        <p className="text-xs text-slate-500 mb-3">CSV columns: <code>network, order_id, merchant, deal_id, sale_amount, commission, currency, status, date, source</code>. Status values like pending / approved / rejected / reversed / paid are mapped automatically. Re-importing updates existing orders.</p>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 text-left"><tr><th className="p-2">Date</th><th className="p-2">Network</th><th className="p-2">Order</th><th className="p-2">Merchant</th><th className="p-2">Sale</th><th className="p-2">Commission</th><th className="p-2">INR</th><th className="p-2">Status</th></tr></thead>
          <tbody>{(c?.items || []).map((x) => (
            <tr key={x.id} className="border-t border-slate-100"><td className="p-2">{fmtDate(x.date)}</td><td className="p-2">{x.network}</td><td className="p-2 font-mono text-xs">{x.order_id}</td><td className="p-2">{x.merchant}</td><td className="p-2">{x.sale_amount}</td><td className="p-2">{x.commission} {x.currency}</td><td className="p-2 font-semibold">{inr(x.commission_inr)}</td><td className="p-2"><StatusBadge s={x.status} /></td></tr>
          ))}</tbody>
        </table></div>
        {c?.imports?.length > 0 && <p className="text-xs text-slate-500 mt-3">Last report import: {fmtDateTime(c.imports[0].at)} by {c.imports[0].by}</p>}
      </Panel>
    </div>
  );
}
